import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Play, Mail, Phone, Facebook, CheckCircle, ArrowRight, Menu, X, ExternalLink, ChevronLeft, ChevronRight, ZoomIn, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ielts-prep-suite/artifacts/q2693oj8_rsz_untitled_design_5-1-Photoroom.png";

// Footer Logo (White version for dark background)
const FOOTER_LOGO_URL = "https://customer-assets.emergentagent.com/job_ielts-prep-suite/artifacts/d7x4trzz_Untitled-design-8-1.webp";

// WhatsApp Number
const WHATSAPP_NUMBER = "+8801346133117";

// Test Interface Sample Images
const TEST_SAMPLES = [
  {
    id: 1,
    title: "Writing Test Interface",
    description: "Task 1 Writing পরীক্ষার interface - Word counter ও text area সহ essay লেখার জন্য",
    url: "https://customer-assets.emergentagent.com/job_ielts-prep-suite/artifacts/enac0q7z_Screenshot%202026-02-25%20013327.png",
    type: "Writing"
  },
  {
    id: 2,
    title: "Reading Test",
    description: "Highlight, Note, Word Drag & Drop সহ সব advanced features - Real exam এর মতো interface",
    url: "https://customer-assets.emergentagent.com/job_ielts-prep-suite/artifacts/g3e5ipst_Screenshot%202026-02-25%20013038.png",
    type: "Reading"
  },
  {
    id: 3,
    title: "Listening Test Interface",
    description: "Audio player সহ Listening test - Real time audio playback ও note completion",
    url: "https://customer-assets.emergentagent.com/job_ielts-prep-suite/artifacts/dzdylhp8_Screenshot%202026-02-25%20012843.png",
    type: "Listening"
  }
];

// Main Exam Feature Video
const EXAM_FEATURE_VIDEO = "https://customer-assets.emergentagent.com/job_ielts-prep-suite/artifacts/co7optvo_Screen%20Recording%202026-02-25%20015326.mp4";

// Header Component
const Header = ({ onContactClick }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="VirtualSwift" className="h-10 md:h-12 w-auto" />
          </Link>
          
          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            <Link 
              to="/" 
              className="text-slate-600 hover:text-sky-500 font-medium transition-colors"
              data-testid="nav-home"
            >
              Home
            </Link>
            <button 
              onClick={() => scrollToSection('mock-test')} 
              className="text-slate-600 hover:text-sky-500 font-medium transition-colors"
              data-testid="nav-mock-test"
            >
              Mock Test
            </button>
            <button 
              onClick={() => scrollToSection('partial-test')} 
              className="text-slate-600 hover:text-sky-500 font-medium transition-colors"
              data-testid="nav-partial-test"
            >
              Partial Test
            </button>
            <button 
              onClick={() => scrollToSection('writing-test')} 
              className="text-slate-600 hover:text-sky-500 font-medium transition-colors"
              data-testid="nav-writing-test"
            >
              Writing Software
            </button>
            <Button 
              onClick={onContactClick}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-full px-6"
              data-testid="nav-contact-btn"
            >
              Contact Us
            </Button>
          </nav>
          
          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-100">
            <div className="flex flex-col gap-4">
              <Link to="/" className="text-left text-slate-600 py-2">Home</Link>
              <button onClick={() => scrollToSection('mock-test')} className="text-left text-slate-600 py-2">Mock Test</button>
              <button onClick={() => scrollToSection('partial-test')} className="text-left text-slate-600 py-2">Partial Test</button>
              <button onClick={() => scrollToSection('writing-test')} className="text-left text-slate-600 py-2">Writing Software</button>
              <Button onClick={onContactClick} className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-full">
                Contact Us
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = ({ onMockClick, onWritingVideoClick, onMockPricingClick }) => {
  return (
    <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 hero-mesh pointer-events-none"></div>
      <div className="absolute top-20 right-0 w-1/2 h-1/2 bg-gradient-to-bl from-sky-400/10 to-transparent rounded-full blur-3xl pointer-events-none"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-sky-50 rounded-full border border-sky-100">
              <span className="w-2 h-2 bg-sky-500 rounded-full animate-pulse"></span>
              <span className="text-sm font-medium text-sky-700">VirtualSwift.com</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
              CD IELTS Test
              <span className="block text-gradient">Solution</span>
            </h1>
            
            <p className="text-lg text-slate-600 max-w-lg leading-relaxed font-['Tiro_Bangla']">
              আপনার কোচিং সেন্টারের জন্য সম্পূর্ণ CD IELTS Mock Test, Partial Test এবং Writing Evaluation Software সলিউশন। 
              <span className="font-semibold text-slate-800"> No server cost, No website development cost!</span>
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={onMockClick}
                className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 transition-all hover:scale-105"
                data-testid="hero-mock-btn"
              >
                Get Mock Solution
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                onClick={onMockPricingClick}
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg shadow-amber-500/25 hover:shadow-xl transition-all hover:scale-105"
                data-testid="hero-mock-pricing-btn"
              >
                Mock Pricing
              </Button>
              <Button 
                variant="outline"
                onClick={onWritingVideoClick}
                className="rounded-full px-8 py-6 text-lg border-slate-200 hover:border-purple-200 hover:bg-purple-50"
                data-testid="hero-writing-btn"
              >
                <Play className="mr-2 w-5 h-5" />
                Writing Test Solution
              </Button>
            </div>
            
            <div className="flex items-center gap-8 pt-4">
              <div>
                <div className="text-2xl font-bold text-slate-900">8+</div>
                <div className="text-sm text-slate-500">Years Experience</div>
              </div>
              <div className="w-px h-12 bg-slate-200"></div>
              <div>
                <div className="text-lg font-semibold text-slate-800">Build Any Kind of</div>
                <div className="text-sm text-slate-500">Software Solution</div>
              </div>
            </div>
          </div>
          
          <div className="relative hidden lg:block">
            <div className="absolute inset-0 bg-gradient-to-tr from-sky-500/20 to-cyan-400/20 rounded-3xl blur-2xl"></div>
            <img 
              src="https://customer-assets.emergentagent.com/job_ielts-prep-suite/artifacts/f8f1xyis_ChatGPT%20Image%20Feb%2025%2C%202026%2C%2002_22_13%20AM.png"
              alt="CD IELTS Test Center"
              className="relative rounded-3xl shadow-2xl w-full object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// Video Embed Component
const VideoEmbed = ({ videoUrl, title }) => {
  const getYouTubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };
  
  const videoId = getYouTubeId(videoUrl);
  
  return (
    <div className="relative aspect-video rounded-2xl overflow-hidden shadow-xl bg-slate-900">
      <iframe
        src={`https://www.youtube.com/embed/${videoId}`}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="absolute inset-0 w-full h-full"
      ></iframe>
    </div>
  );
};

// Pricing Card Component
const PricingCard = ({ title, price, features, highlight, badge }) => {
  return (
    <div className={`relative p-6 rounded-2xl transition-all hover:scale-105 ${
      highlight 
        ? 'bg-slate-900 text-white border-2 border-sky-500/50 shadow-xl shadow-sky-500/10' 
        : 'bg-white border border-slate-200 hover:border-sky-200 hover:shadow-lg'
    }`}>
      {badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-amber-500 text-white text-xs font-bold rounded-full">
          {badge}
        </div>
      )}
      <div className="text-center mb-4">
        <h4 className={`font-semibold mb-2 ${highlight ? 'text-white' : 'text-slate-800'}`}>{title}</h4>
        <div className={`text-2xl font-bold ${highlight ? 'text-sky-400' : 'text-sky-500'}`}>{price}</div>
      </div>
      <ul className="space-y-2">
        {features.map((feature, idx) => (
          <li key={idx} className="flex items-start gap-2 text-sm">
            <CheckCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${highlight ? 'text-sky-400' : 'text-sky-500'}`} />
            <span className={highlight ? 'text-slate-300' : 'text-slate-600'}>{feature}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

// Mock Test Package Section with Samples Gallery
const MockTestSection = ({ onContactClick }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setSelectedImage(TEST_SAMPLES[index]);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const goToPrevious = () => {
    const newIndex = currentIndex === 0 ? TEST_SAMPLES.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    setSelectedImage(TEST_SAMPLES[newIndex]);
  };

  const goToNext = () => {
    const newIndex = currentIndex === TEST_SAMPLES.length - 1 ? 0 : currentIndex + 1;
    setCurrentIndex(newIndex);
    setSelectedImage(TEST_SAMPLES[newIndex]);
  };

  return (
    <section id="mock-test" className="py-20 lg:py-32 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium mb-6">
            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
            Package 1
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Mock Test Package
          </h2>
          
          <p className="text-lg text-slate-600 max-w-3xl mx-auto font-['Tiro_Bangla']">
            Full CD IELTS mock test upload এবং management solution। আপনার students-দের জন্য complete simulation environment।
          </p>
        </div>

        {/* Demo Video */}
        <div className="mb-16">
          <div className="max-w-4xl mx-auto">
            <VideoEmbed 
              videoUrl="https://www.youtube.com/watch?v=xdBG9m0UnJs" 
              title="Mock Test Demo"
            />
          </div>
          
          <div className="mt-6 max-w-3xl mx-auto p-4 bg-sky-50 rounded-xl border border-sky-100">
            <p className="text-sky-800 font-medium flex items-start justify-center gap-2 font-['Tiro_Bangla']">
              <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <span><strong>Best Advantage:</strong> No server cost, No Website Development & maintenance cost। শুধুমাত্র test attempt হলেই pay করতে হবে!</span>
            </p>
          </div>
        </div>

        {/* Main Exam Features Video */}
        <div className="mb-16 pt-12 border-t border-slate-200">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium mb-4">
              <Play className="w-3 h-3" />
              Main Exam Feature Demo
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
              Real Exam এর মতো Features
            </h3>
            <p className="text-slate-600 max-w-2xl mx-auto font-['Tiro_Bangla']">
              আমাদের software এ main IELTS exam এর সব features আছে - দেখুন কিভাবে কাজ করে।
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl bg-slate-900 border border-slate-700">
              <video 
                src={EXAM_FEATURE_VIDEO}
                controls
                className="w-full h-full object-cover"
                poster=""
              >
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>

        {/* Test Interface Samples Gallery */}
        <div id="samples" className="mb-16 pt-12 border-t border-slate-200">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-sky-100 text-sky-700 rounded-full text-sm font-medium mb-4">
              <ZoomIn className="w-3 h-3" />
              Test Interface Preview
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
              Test Interface Samples
            </h3>
            <p className="text-slate-600 max-w-2xl mx-auto font-['Tiro_Bangla']">
              আমাদের Listening, Reading এবং Writing test interface দেখুন। বড় করে দেখতে image এ click করুন।
            </p>
          </div>
          
          <div className="grid sm:grid-cols-3 gap-6">
            {TEST_SAMPLES.map((sample, index) => (
              <div 
                key={sample.id}
                className="group relative bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all cursor-pointer border border-slate-100"
                onClick={() => openLightbox(index)}
                data-testid={`sample-card-${sample.id}`}
              >
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={sample.url} 
                    alt={sample.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-4">
                  <div className="flex items-center gap-2 text-white">
                    <ZoomIn className="w-5 h-5" />
                    <span className="text-sm font-medium font-['Tiro_Bangla']">বড় করে দেখুন</span>
                  </div>
                </div>
                <div className="p-4">
                  <div className="inline-block px-2 py-1 bg-sky-100 text-sky-700 text-xs font-medium rounded mb-2">
                    {sample.type}
                  </div>
                  <h4 className="font-semibold text-slate-800 text-sm mb-1">{sample.title}</h4>
                  <p className="text-xs text-slate-500 font-['Tiro_Bangla']">{sample.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pricing Section - At the end for funnel */}
        <div className="pt-12 border-t border-slate-200">
          <div className="text-center mb-10">
            <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">Mock Product Making Charge</h3>
            <p className="text-slate-500">(One time fee)</p>
          </div>
          
          <div id="mock-pricing" className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-8">
            <PricingCard 
              title="5 Mock Test"
              price="BDT 50,000/-"
              features={['AI & Instructor Writing Paper checking', 'Automatic result send', 'Details feedback page', 'Unlimited Student Account']}
            />
            <PricingCard 
              title="10 Mock Test"
              price="BDT 55,000/-"
              features={['AI & Instructor Writing Paper checking', 'Automatic result send', 'Details feedback page', 'Unlimited Student Account']}
              highlight
              badge="POPULAR"
            />
            <PricingCard 
              title="20 Mock Test"
              price="BDT 70,000/-"
              features={['AI & Instructor Writing Paper checking', 'Automatic result send', 'Details feedback page', 'Unlimited Student Account']}
            />
          </div>
          
          <div className="max-w-xl mx-auto space-y-4">
            <div className="p-4 bg-sky-50 rounded-xl border border-sky-200 text-center">
              <p className="text-sky-800 font-semibold">One Time Payment — কোনো মাসিক চার্জ নেই</p>
            </div>
            
            <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200 text-center">
              <p className="text-emerald-800 font-semibold font-['Tiro_Bangla']">
                Complete White Label Branding সুবিধা সহ
              </p>
            </div>
            
            <Button 
              onClick={onContactClick}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-full py-6 text-lg font-semibold shadow-lg shadow-blue-500/25"
              data-testid="mock-test-contact-btn"
            >
              Get This Package
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Lightbox Modal */}
        <Dialog open={!!selectedImage} onOpenChange={closeLightbox}>
          <DialogContent className="max-w-5xl p-0 bg-slate-900/95 border-slate-700">
            <div className="relative">
              {/* Navigation */}
              <button 
                onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
                data-testid="lightbox-prev"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button 
                onClick={(e) => { e.stopPropagation(); goToNext(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
                data-testid="lightbox-next"
              >
                <ChevronRight className="w-6 h-6" />
              </button>

              {/* Image */}
              <div className="p-4 pt-10">
                <img 
                  src={selectedImage?.url} 
                  alt={selectedImage?.title}
                  className="w-full h-auto max-h-[70vh] object-contain rounded-lg"
                />
              </div>

              {/* Info */}
              <div className="p-6 text-center border-t border-slate-700">
                <div className="inline-block px-3 py-1 bg-sky-500/20 text-sky-400 text-sm font-medium rounded-full mb-2">
                  {selectedImage?.type}
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{selectedImage?.title}</h3>
                <p className="text-slate-400 font-['Tiro_Bangla']">{selectedImage?.description}</p>
                <div className="mt-4 text-sm text-slate-500">
                  {currentIndex + 1} / {TEST_SAMPLES.length}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

// Partial Test Section
const PartialTestSection = ({ onContactClick }) => {
  return (
    <section id="partial-test" className="py-20 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="p-6 bg-gradient-to-br from-sky-500 to-cyan-500 rounded-2xl text-white">
                <h4 className="text-lg font-semibold mb-2">10 Tests</h4>
                <p className="text-sm opacity-90 mb-3">(Listening or Reading)</p>
                <div className="text-3xl font-bold">12,000 BDT</div>
              </div>
              <div className="p-6 bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl text-white">
                <h4 className="text-lg font-semibold mb-2">20 Tests</h4>
                <p className="text-sm opacity-90 mb-3">(Listening or Reading)</p>
                <div className="text-3xl font-bold">20,000 BDT</div>
              </div>
            </div>
            
            <div className="mt-6 space-y-4">
              <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl">
                <CheckCircle className="w-5 h-5 text-sky-500 mt-0.5" />
                <div>
                  <p className="font-medium text-slate-800">Minimum 5 Tests Required</p>
                  <p className="text-sm text-slate-500 font-['Tiro_Bangla']">শুরু করতে কমপক্ষে ৫টি test প্রয়োজন</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 bg-green-50 rounded-xl border border-green-100">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                <div>
                  <p className="font-medium text-green-800">✅ White Label Branding Available</p>
                  <p className="text-sm text-green-600 font-['Tiro_Bangla']">আপনার নিজস্ব branding সহ solution</p>
                </div>
              </div>
            </div>
            
            <Button 
              onClick={onContactClick}
              className="mt-6 w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-full py-6 text-lg font-semibold shadow-lg shadow-blue-500/25"
              data-testid="partial-test-contact-btn"
            >
              Get This Package
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
          
          <div className="order-1 lg:order-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
              Package 2
            </div>
            
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              Partial Test Package
              <span className="block text-lg md:text-xl text-slate-500 font-normal mt-2">(Listening & Reading)</span>
            </h2>
            
            <p className="text-lg text-slate-600 mb-6 font-['Tiro_Bangla']">
              Listening এবং Reading আলাদা ভাবে পরিচালনা করার জন্য suitable। Module-specific practice করতে চাইলে এটি perfect choice।
            </p>
            
            <ul className="space-y-3">
              {['Module specific practice', 'Instant scoring system', 'Detailed analytics', 'Student progress tracking'].map((item, idx) => (
                <li key={idx} className="flex items-center gap-3 text-slate-700">
                  <CheckCircle className="w-5 h-5 text-sky-500" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

// Writing Test Section
const WritingTestSection = ({ onContactClick, onFullDemoClick }) => {
  return (
    <section id="writing-test" className="py-20 lg:py-32 bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm font-medium mb-6">
            <span className="w-2 h-2 bg-purple-400 rounded-full"></span>
            Package 3
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Writing Test Software
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto font-['Tiro_Bangla']">
            Complete CD Writing Management System — Self test creation থেকে automated feedback পর্যন্ত সব কিছু।
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="relative aspect-video rounded-2xl overflow-hidden shadow-xl bg-slate-800 border border-slate-700">
              <iframe
                src="https://www.youtube.com/embed/XQFR4FbUHWs"
                title="Writing Test Promo"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
              ></iframe>
            </div>
            
            {/* Full Demo Button - Bigger and clickable to open lightbox */}
            <div className="mt-8">
              <button 
                onClick={onFullDemoClick}
                className="w-full p-4 bg-gradient-to-r from-purple-600/30 to-sky-600/30 hover:from-purple-600/40 hover:to-sky-600/40 border border-purple-500/40 rounded-xl transition-all group cursor-pointer"
                data-testid="writing-full-demo-btn"
              >
                <p className="text-slate-300 text-sm mb-1">Full Demo</p>
                <div className="flex items-center justify-center gap-3 text-white">
                  <Play className="w-6 h-6 text-sky-400 group-hover:text-sky-300 transition-colors" />
                  <span className="text-lg font-semibold">Watch Full Demo Video</span>
                </div>
              </button>
            </div>
          </div>
          
          <div>
            <div className="bg-gradient-to-br from-purple-500/20 to-sky-500/20 rounded-3xl p-8 border border-purple-500/30">
              <h3 className="text-xl font-semibold mb-6">Key Features</h3>
              
              <div className="space-y-4">
                {[
                  { title: 'Self Test Creation Access', desc: 'আপনি নিজেই unlimited Writing test তৈরি ও manage করতে পারবেন।' },
                  { title: 'Student Submission Tracking', desc: 'প্রতিটি student submission organized ভাবে track করা যাবে।' },
                  { title: 'Instructor Management Panel', desc: 'Instructor আলাদা panel থেকে marking ও feedback দিতে পারবে।' },
                  { title: 'Automated Email Feedback Link', desc: 'Marking complete হলে student-এর email-এ automatic feedback link চলে যাবে।' },
                  { title: 'Voice Recorded Feedback', desc: 'Written feedback-এর পাশাপাশি voice explanation দেওয়ার সুবিধা থাকবে।' },
                ].map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-sky-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-white">{feature.title}</p>
                      <p className="text-sm text-slate-400 font-['Tiro_Bangla']">{feature.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 p-6 bg-slate-800/50 rounded-2xl text-center">
                <p className="text-slate-400 mb-2 font-['Tiro_Bangla']">One time payment, get yours for life time</p>
                <div className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-purple-400">
                  28,000 BDT
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-amber-500/20 rounded-xl">
                <p className="text-amber-200 text-sm text-center font-['Tiro_Bangla']">
                  ⚠️ Note: Server cost included না (Hosting আলাদা লাগবে)
                </p>
              </div>
              
              <Button 
                onClick={onContactClick}
                className="mt-6 w-full bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 hover:from-blue-700 hover:via-blue-800 hover:to-indigo-800 text-white rounded-full py-6 text-lg font-semibold"
                data-testid="writing-test-contact-btn"
              >
                Get This Package
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Modal Component
const ContactModal = ({ open, onOpenChange, selectedPackage }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    institute_name: '',
    phone: '',
    email: '',
    package_interest: '',
    message: ''
  });

  // Update package_interest when selectedPackage changes
  React.useEffect(() => {
    if (selectedPackage) {
      setFormData(prev => ({ ...prev, package_interest: selectedPackage }));
    }
  }, [selectedPackage, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Thank you! We will contact you soon.', {
        description: 'আপনার message পাঠানো হয়েছে।'
      });
      onOpenChange(false);
      setFormData({
        name: '',
        institute_name: '',
        phone: '',
        email: '',
        package_interest: '',
        message: ''
      });
    } catch (error) {
      toast.error('Something went wrong', {
        description: 'Please try again or contact us directly.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-slate-900">Contact Us</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label htmlFor="name">Name *</Label>
            <Input 
              id="name"
              required
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="আপনার নাম"
              className="font-['Tiro_Bangla']"
              data-testid="contact-name-input"
            />
          </div>
          
          <div>
            <Label htmlFor="institute">Institute Name</Label>
            <Input 
              id="institute"
              value={formData.institute_name}
              onChange={(e) => setFormData({...formData, institute_name: e.target.value})}
              placeholder="প্রতিষ্ঠানের নাম"
              className="font-['Tiro_Bangla']"
              data-testid="contact-institute-input"
            />
          </div>
          
          <div>
            <Label htmlFor="phone">Phone *</Label>
            <Input 
              id="phone"
              required
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              placeholder="01XXXXXXXXX"
              data-testid="contact-phone-input"
            />
          </div>
          
          <div>
            <Label htmlFor="email">Email *</Label>
            <Input 
              id="email"
              required
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              placeholder="your@email.com"
              data-testid="contact-email-input"
            />
          </div>
          
          <div>
            <Label htmlFor="package">Package Interest *</Label>
            <Select 
              value={formData.package_interest} 
              onValueChange={(value) => setFormData({...formData, package_interest: value})}
            >
              <SelectTrigger data-testid="contact-package-select">
                <SelectValue placeholder="Select a package" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="mock-test">Mock Test Package</SelectItem>
                <SelectItem value="partial-test">Partial Test Package</SelectItem>
                <SelectItem value="writing-test">Writing Test Software</SelectItem>
                <SelectItem value="custom">Custom Solution</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label htmlFor="message">Message</Label>
            <Textarea 
              id="message"
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              placeholder="আপনার প্রশ্ন বা requirements..."
              rows={3}
              className="font-['Tiro_Bangla']"
              data-testid="contact-message-input"
            />
          </div>
          
          <Button 
            type="submit" 
            disabled={loading}
            className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-full py-6"
            data-testid="contact-submit-btn"
          >
            {loading ? 'Sending...' : 'Send Message'}
          </Button>
        </form>
        
        <div className="mt-4 pt-4 border-t border-slate-100">
          <p className="text-sm text-slate-500 mb-3 font-['Tiro_Bangla']">অথবা সরাসরি যোগাযোগ করুন:</p>
          <div className="flex flex-wrap gap-3">
            <a 
              href="mailto:info@virtualswift.com" 
              className="inline-flex items-center gap-2 text-sm text-sky-600 hover:text-sky-700"
              data-testid="contact-email-link"
            >
              <Mail className="w-4 h-4" />
              info@virtualswift.com
            </a>
            <a 
              href="tel:01346133117" 
              className="inline-flex items-center gap-2 text-sm text-sky-600 hover:text-sky-700"
              data-testid="contact-phone-link"
            >
              <Phone className="w-4 h-4" />
              01346133117
            </a>
            <a 
              href="https://www.facebook.com/virtualswift1" 
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-sky-600 hover:text-sky-700"
              data-testid="contact-facebook-link"
            >
              <Facebook className="w-4 h-4" />
              Facebook
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Footer Component
const Footer = ({ onContactClick }) => {
  return (
    <footer className="bg-slate-900 text-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-3 gap-12">
          <div>
            <img src={FOOTER_LOGO_URL} alt="VirtualSwift" className="h-14 mb-4" />
            <p className="text-slate-400 font-['Tiro_Bangla']">
              Digital service এবং full stack software solutions provider। আপনার IELTS coaching center-এর জন্য complete CD test solution।
            </p>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4">Services</h4>
            <ul className="space-y-2 text-slate-400">
              <li><a href="#mock-test" className="hover:text-sky-400 transition-colors">Mock Test Package</a></li>
              <li><a href="#partial-test" className="hover:text-sky-400 transition-colors">Partial Test Package</a></li>
              <li><a href="#writing-test" className="hover:text-sky-400 transition-colors">Writing Test Software</a></li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold mb-4">Contact</h4>
            <ul className="space-y-3 text-slate-400">
              <li>
                <a href="mailto:info@virtualswift.com" className="flex items-center gap-2 hover:text-sky-400 transition-colors">
                  <Mail className="w-4 h-4" />
                  info@virtualswift.com
                </a>
              </li>
              <li>
                <a href="tel:01346133117" className="flex items-center gap-2 hover:text-sky-400 transition-colors">
                  <Phone className="w-4 h-4" />
                  01346133117
                </a>
              </li>
              <li>
                <a href="https://www.facebook.com/virtualswift1" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-sky-400 transition-colors">
                  <Facebook className="w-4 h-4" />
                  facebook.com/virtualswift1
                </a>
              </li>
              <li>
                <a 
                  href={`https://wa.me/${WHATSAPP_NUMBER.replace('+', '')}`}
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center gap-2 hover:text-green-400 transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp Chat
                </a>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="mt-12 pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} VirtualSwift. All rights reserved.
          </p>
          <Button 
            onClick={onContactClick}
            variant="outline"
            className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white rounded-full"
            data-testid="footer-contact-btn"
          >
            Contact Us
          </Button>
        </div>
      </div>
    </footer>
  );
};

// WhatsApp Floating Button Component
const WhatsAppFloatingButton = () => {
  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER.replace('+', '')}?text=${encodeURIComponent('Hi, I am interested in your CD IELTS Test Solution.')}`;
  
  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 right-6 z-50 flex items-center gap-3 bg-green-500 hover:bg-green-600 text-white px-4 py-3 rounded-full shadow-lg shadow-green-500/30 hover:shadow-xl hover:shadow-green-500/40 transition-all hover:scale-105 group"
      data-testid="whatsapp-floating-btn"
    >
      <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
      <span className="font-medium hidden sm:block">Live Chat</span>
    </a>
  );
};

// Main Landing Page
const LandingPage = () => {
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState('');
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [fullDemoModalOpen, setFullDemoModalOpen] = useState(false);

  const openContactModal = (packageType = '') => {
    setSelectedPackage(packageType);
    setContactModalOpen(true);
  };

  const scrollToRealExamFeatures = () => {
    // Scroll to the Real Exam Features section within Mock Test
    document.getElementById('mock-test')?.scrollIntoView({ behavior: 'smooth' });
    // Small delay then scroll a bit more to get to Real Exam Features
    setTimeout(() => {
      const mockSection = document.getElementById('mock-test');
      if (mockSection) {
        window.scrollBy({ top: 600, behavior: 'smooth' });
      }
    }, 500);
  };

  // Disable right-click and dev tools shortcuts
  React.useEffect(() => {
    // Disable right-click
    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Disable keyboard shortcuts
    const handleKeyDown = (e) => {
      // Disable F12
      if (e.key === 'F12') {
        e.preventDefault();
        return false;
      }
      // Disable Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+Shift+C
      if (e.ctrlKey && e.shiftKey && ['I', 'J', 'C'].includes(e.key.toUpperCase())) {
        e.preventDefault();
        return false;
      }
      // Disable Ctrl+U (View Source)
      if (e.ctrlKey && e.key.toUpperCase() === 'U') {
        e.preventDefault();
        return false;
      }
      // Disable Ctrl+S (Save)
      if (e.ctrlKey && e.key.toUpperCase() === 'S') {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <div className="min-h-screen" data-testid="landing-page">
      <Header onContactClick={() => openContactModal()} />
      <HeroSection 
        onMockClick={scrollToRealExamFeatures}
        onWritingVideoClick={() => setVideoModalOpen(true)}
        onMockPricingClick={() => document.getElementById('mock-pricing')?.scrollIntoView({ behavior: 'smooth' })}
      />
      <MockTestSection onContactClick={() => openContactModal('mock-test')} />
      <PartialTestSection onContactClick={() => openContactModal('partial-test')} />
      <WritingTestSection 
        onContactClick={() => openContactModal('writing-test')} 
        onFullDemoClick={() => setFullDemoModalOpen(true)}
      />
      <Footer onContactClick={() => openContactModal()} />
      
      {/* WhatsApp Floating Button */}
      <WhatsAppFloatingButton />
      
      <ContactModal 
        open={contactModalOpen} 
        onOpenChange={setContactModalOpen}
        selectedPackage={selectedPackage}
      />

      {/* Video Modal for Writing Test Solution (Hero button) */}
      <Dialog open={videoModalOpen} onOpenChange={setVideoModalOpen}>
        <DialogContent className="max-w-4xl p-0 bg-slate-900/95 border-slate-700">
          <div className="relative">
            <div className="p-4 pt-10">
              <div className="aspect-video rounded-lg overflow-hidden">
                <iframe
                  src="https://www.youtube.com/embed/XQFR4FbUHWs?autoplay=1"
                  title="Writing Test Solution"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              </div>
            </div>
            <div className="p-4 text-center">
              <h3 className="text-lg font-semibold text-white">Writing Test Solution Demo</h3>
              <p className="text-slate-400 text-sm font-['Tiro_Bangla']">CD IELTS Writing Test Software এর সম্পূর্ণ demo দেখুন</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Full Demo Video Modal (from Writing Test Section) */}
      <Dialog open={fullDemoModalOpen} onOpenChange={setFullDemoModalOpen}>
        <DialogContent className="max-w-5xl p-0 bg-slate-900/95 border-slate-700">
          <div className="relative">
            <div className="p-4 pt-10">
              <div className="aspect-video rounded-lg overflow-hidden">
                <iframe
                  src={fullDemoModalOpen ? "https://www.youtube.com/embed/XFYJJCmfw5k?autoplay=1" : ""}
                  title="Writing Test Full Demo"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              </div>
            </div>
            <div className="p-4 text-center">
              <h3 className="text-xl font-semibold text-white">Full Demo - Writing Test Software</h3>
              <p className="text-slate-400 font-['Tiro_Bangla']">CD IELTS Writing Test Software এর সম্পূর্ণ features এবং functionality দেখুন</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;
